import { useEffect, useState } from "react";

import { Spinner } from "@blueprintjs/core";
import { useHead } from "@unhead/react";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router";

import { useCurrentSession } from "@/components/helpers/custom-hooks/use-current-session";
import { useCurrentUser } from "@/components/helpers/custom-hooks/use-current-user";
import { SEOHead } from "@/components/helpers/seo/SEOHead";
import { buildUrl } from "@/components/helpers/url-utils";
import { Box, Flex } from "@/components/layout/flexbox";
import { isXsToLgQuery, Media } from "@/components/layout/Media";
import { PageLoadingIndicator } from "@/components/nav/PageLoading";
import {
  FINDING_STEP_HASH,
  useMatchSteps,
} from "@/components/pages_flexible/match/specialty/useMatchSteps";
import { Authenticate } from "@/components/pages_logged_in/Authenticate";
import { AffirmTag } from "@/components/pieces/buy-credits/AffirmTag";
import { BackButton } from "@/components/pieces/onboarding/BackButton";
import { Header } from "@/components/pieces/onboarding/Header";
import { ProgressBar } from "@/components/pieces/onboarding/ProgressBar";
import { LIGHTEST_BACKGROUND, SITE_NAME } from "@/css/constants";
import {
  FindMyAgentQuery,
  IntakeFormInput,
  SaveIntakeFormMutation,
  useFindMyAgentQuery,
  useSaveIntakeFormMutation,
} from "@/graphql";

interface MatchFlowProps {
  currentUser: ReturnType<typeof useCurrentUser>;
  intakeForm?: NonNullable<
    FindMyAgentQuery["optionalCurrentUser"]
  >["intakeForm"];
  loggedIn: boolean;
}

function MatchFlow({
  currentUser,
  loggedIn,
  intakeForm: intakeFormData,
}: MatchFlowProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { specialtySlug } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const productSlugs = searchParams.get("p");
  const programComponentId = searchParams.get("programComponentId");

  const intakeForm = intakeFormData || {
    id: "",
    industries: [],
    jobTitles: [],
    jobFunctions: [],
    industriesSkipped: false,
    jobTitlesSkipped: false,
    jobFunctionsSkipped: false,
    matchingOptions: [],
  };

  const [orderId, setOrderId] = useState(searchParams.get("oid"));
  const [token, setToken] = useState(
    searchParams.has("token")
      ? searchParams.get("token")
      : intakeForm
        ? intakeForm.token
        : null,
  );

  const { step, stepIndex, matchSteps, goToStep, goBackStep } =
    useMatchSteps(specialtySlug);

  const prevStep = matchSteps[stepIndex - 1];
  const {
    category: stepCategory,
    render: renderStep,
    hash: stepHash,
  } = step || {};

  function goToComplete() {
    // If a user is not logged in, redirect them to sign up first
    if (!currentUser) {
      const redirectUrl = buildUrl(`/signup`, {
        data: {
          v: "match", // version
          back: buildUrl(`/match/${specialtySlug}`, {
            data: { oid: orderId, token, p: productSlugs },
          }),
          nextPath: buildUrl(`/match/${specialtySlug}`, {
            data: { oid: orderId, token, p: productSlugs },
          }),
        },
      });
      navigate(redirectUrl, { replace: true });
      return null;
    }

    // Otherwise, take the user to their results!

    navigate(
      buildUrl(`/choose/${specialtySlug}`, {
        data: {
          oid: orderId,
          p: productSlugs,
          programComponentId,
          back: buildUrl(`/match/${specialtySlug}`, {
            data: { oid: orderId, p: productSlugs, programComponentId },
          }),
        },
      }),
    );
  }

  // On save, we update our local state with the references we need
  // and advance the user to the next step.
  function onSaveCompleted(r?: SaveIntakeFormMutation) {
    // The only case where we have a token in the URL and have a current
    // user is when we are associating the intake form with the current
    // user. In that case, replace the URL and complete the process.
    if (searchParams.has("token")) {
      // Replace the URL with a version that doesn't have the token in it
      // so the back button works properly
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete("token");
      setSearchParams(newSearchParams);
      navigate(
        buildUrl(location.pathname, {
          data: Object.fromEntries(newSearchParams),
        }),
        {
          replace: true,
        },
      );
      if (currentUser) {
        goToComplete();
        return;
      }
    }
    if (r?.saveIntakeForm?.token) {
      setToken(r.saveIntakeForm.token);
    }
    if (r?.saveIntakeForm?.orderId) {
      setOrderId(r.saveIntakeForm.orderId);
    }
    const nextStep = matchSteps[stepIndex + 1];
    if (nextStep) {
      goToStep(nextStep);
    } else {
      goToComplete();
    }
  }
  const [saveIntakeForm, { loading: saving }] = useSaveIntakeFormMutation({
    onCompleted: onSaveCompleted,
    update: (cache) => {
      cache.evict({ fieldName: "agentProfiles" });
      cache.evict({ fieldName: "optionalCurrentUser" });
      cache.gc();
    },
  });

  function initSaveIntakeForm(input?: IntakeFormInput) {
    // If this function is called with no parameters,
    // skip the API call and just go to the callback
    if (!input) {
      onSaveCompleted();
      return;
    }
    saveIntakeForm({
      variables: {
        token: token || "",
        input: { specialtySlug, ...input },
        orderId: orderId || "",
      },
    });
  }

  // Make sure that the back and forward buttons work the
  // way that the user expects them to
  useEffect(() => {
    const newHash = location.hash.replace("#", "");
    if (stepHash && stepHash !== newHash) {
      const newStep = matchSteps.find((s) => s.hash === newHash);
      if (newStep) {
        goToStep(newStep, true);
      }
    }
  }, [location.hash, stepHash, matchSteps, goToStep]);

  // if the order ID changes, make sure the URL contains
  // the correct order id
  useEffect(() => {
    if (searchParams.get("oid") !== orderId) {
      const newSearchParams = new URLSearchParams(searchParams);
      navigate(
        {
          pathname: location.pathname,
          search: newSearchParams.toString(),
          hash: location.hash,
        },
        { replace: true },
      );
    }
  }, [orderId]);

  // If a token was passed in via the URL parameter,
  // try to link up the IntakeForm associated with the token
  // and the current user
  useEffect(() => {
    if (searchParams.has("token")) {
      initSaveIntakeForm({});
    }
  }, []);

  const content = searchParams.has("token") ? (
    <Flex justifyContent="center">
      <Spinner size={24} />
    </Flex>
  ) : renderStep ? (
    renderStep({
      loading: saving,
      intakeForm,
      saveIntakeForm: initSaveIntakeForm,
      category: stepCategory,
      specialtySlug,
    })
  ) : null;

  const backButton = step && (
    <BackButton
      hoverMessage={
        stepIndex === 0 ? "Coaching type" : prevStep.category?.description
      }
      onClick={stepIndex === 0 ? () => navigate("/match") : () => goBackStep()}
      mediaQuery={isXsToLgQuery}
      mt={[0, 12]}
    />
  );

  useHead({
    bodyAttrs: { style: { backgroundColor: LIGHTEST_BACKGROUND } },
  });

  return (
    <>
      <SEOHead
        title={`Find Your Coach - ${SITE_NAME}`}
        canonicalPath={`/match/${specialtySlug}`}
      />
      <Media at="xs">
        <Header linkToDash={loggedIn} backButton={backButton} />
        <ProgressBar
          width={(stepIndex + 1) / (matchSteps.length + 1)}
          animating={stepHash === FINDING_STEP_HASH}
        />
      </Media>
      <Media greaterThan="xs">
        <Header linkToDash={loggedIn} />
        <ProgressBar
          width={(stepIndex + 1) / (matchSteps.length + 1)}
          animating={stepHash === FINDING_STEP_HASH}
        />
      </Media>
      <Box my={[70, 110, 130]} px={[3, 3, 0]} css={{ position: "relative" }}>
        <Media greaterThan="xs">{backButton}</Media>
        {content}
      </Box>
    </>
  );
}

function MatchFlowLoader({ loggedIn = false }) {
  const { data, loading } = useFindMyAgentQuery();
  const currentUser = useCurrentUser();

  return (
    <>
      {loading ? (
        <>
          <Media at="xs">
            <Header linkToDash={loggedIn} />
          </Media>
          <Media greaterThan="xs">
            <Header linkToDash={loggedIn} />
          </Media>
          <Flex my={[70, 110, 130]} justifyContent="center">
            <Spinner size={24} />
          </Flex>
        </>
      ) : (
        <MatchFlow
          loggedIn={loggedIn}
          intakeForm={data?.optionalCurrentUser?.intakeForm}
          currentUser={currentUser}
        />
      )}
    </>
  );
}

export function SpecialtySelectedFlow() {
  const { currentSessionLink, sessionLoaded } = useCurrentSession();
  if (!sessionLoaded) return <PageLoadingIndicator type="compact" />;
  return currentSessionLink ? (
    <Authenticate layout="compact" showMessageBar={false} maxWidth={"100%"}>
      <MatchFlowLoader loggedIn={true} />
      <AffirmTag />
    </Authenticate>
  ) : (
    <MatchFlowLoader loggedIn={false} />
  );
}
